import React, { useState, useRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { BOARD_TYPE, TOOL_PEN } from '../../../constants';
import {
    Badge,
    Fab,
    Hidden,
    IconButton,
    Snackbar,
    Popover,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    TextField,
    Paper,
    Box,
    IconButton as MuiIconButton,
    makeStyles,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import boardManager from '../../../pages/LessonPage/boardManager';
import lessonList from '../../../pages/LessonPage/lessonList';
import { addLatexToWindowCache } from '../../../common/utils';
import { GA_EVENT_TREE } from '../../../gaConstants';
import MultiBoardIcon from '../../icons/MultiBoardIcon';
import store from '../../../redux/store';
import { changeTool, raiseHand, shareLesson, userJoinRequest, upgradeSubscriptionDialog } from '../../../redux/actions';
import { CAPABILITIES, hasBooleanPermission } from '../../../shared/Capabilities';
import HandRaisedIcon from '../../icons/HandRaisedIcon';
import socketUtils from '../../../pages/LessonPage/socketUtils';
import UKIcon from '../../icons/UK';
import RomaniaIcon from '../../icons/Romania';
import FranceIcon from '../../icons/France';
import AIAssistantIcon from '../../icons/AIAssistantIcon';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import DraftsIcon from '@material-ui/icons/Drafts';
import { Pause, PlayArrow, Stop } from '@material-ui/icons';
import VBToolTip from '../../Tooltip/VBToolTip';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import aiApi from '../../../Api/aiApi';
import { resolveIllustration } from '../../../common/aiIllustration';
import MuiAlert from '@material-ui/lab/Alert';
import AIResponseRenderer from '../../AIResponseRenderer/AIResponseRenderer';

const useAiSelectStyles = makeStyles(() => ({
    select: {
        borderRadius: '16px !important',
        height: '32px',
        fontSize: '0.75rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '16px !important',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderRadius: '16px !important',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderRadius: '16px !important',
        },
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            borderRadius: '16px !important',
        },
        '& .MuiSelect-select:focus': {
            borderRadius: '16px !important',
            backgroundColor: 'transparent',
        },
    },
}));

const ActionButtons = (props) => {
    const aiSelectClasses = useAiSelectStyles();
    const [aiLoading, setAiLoading] = useState(false);
    const [aiAnchorEl, setAiAnchorEl] = useState(null);
    const [aiResponse, setAiResponse] = useState(null);
    const [aiResponseDialogOpen, setAiResponseDialogOpen] = useState(false);
    const [aiSnackbar, setAiSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [aiLanguage, setAiLanguage] = useState(props.intl.locale || 'ro');
    const [aiSubject, setAiSubject] = useState('math');
    const [aiGradeLevel, setAiGradeLevel] = useState('6');
    const [aiType, setAiType] = useState('simple');
    const [aiPrompt, setAiPrompt] = useState('');
    // Illustrations resolved while previewing, keyed by component index, so "Add to
    // board" reuses those pixels instead of paying for a second generation.
    const aiResolvedImagesRef = useRef({});

    const raiseHandClick = () => {
        socketUtils.sendRaisedHandValue(!props.lessonState.raisedHand);
        store.dispatch(raiseHand());
    };

    const handleAiButtonClick = (event) => {
        const hasAI = hasBooleanPermission(
            props.subscriptions,
            props.profile.subscription,
            CAPABILITIES.MATH_CONVERTER,
        );

        if (!hasAI.value) {
            store.dispatch(
                upgradeSubscriptionDialog({
                    key: 'subscription.upsell.mathConverter.unavailable',
                    subscriptions: hasAI.availableSubscriptions,
                }),
            );
            return;
        }

        setAiAnchorEl(event.currentTarget);
        setAiResponse(null);
        setAiPrompt('');
        aiResolvedImagesRef.current = {};
    };

    const handleSendPrompt = async () => {
        if (!aiPrompt.trim()) {
            setAiSnackbar({
                open: true,
                message: 'Please enter a prompt',
                severity: 'warning',
            });
            return;
        }

        setAiLoading(true);
        try {
            let response;
            const options = {
                gradeLevel: aiGradeLevel,
                language: aiLanguage,
                topic: aiPrompt.trim(),
            };

            if (aiType === 'simple') {
                response = await aiApi.generateSimpleExercise(aiSubject, options);
                // Automatically add to board without showing in popover
                await handleAddToBoard(response);
            } else if (aiType === 'solution') {
                response = await aiApi.generateExerciseWithSolution(aiSubject, options);
                setAiResponse(response);
                // Close popover and open dialog
                setAiAnchorEl(null);
                setAiResponseDialogOpen(true);
            } else if (aiType === 'concept') {
                response = await aiApi.explainConcept(aiSubject, aiPrompt.trim(), options);
                setAiResponse(response);
                // Close popover and open dialog
                setAiAnchorEl(null);
                setAiResponseDialogOpen(true);
            }
        } catch (error) {
            console.error('Error generating AI response', error);
            setAiSnackbar({
                open: true,
                message: props.intl.formatMessage({ id: 'ai.error' }),
                severity: 'error',
            });
        } finally {
            setAiLoading(false);
        }
    };

    const handleCloseAiPopover = () => {
        setAiAnchorEl(null);
        setAiResponse(null);
        setAiPrompt('');
        aiResolvedImagesRef.current = {};
    };

    const handleCloseSnackbar = () => {
        setAiSnackbar({ ...aiSnackbar, open: false });
    };

    const handleImageResolved = (index, entry) => {
        aiResolvedImagesRef.current[index] = entry;
    };

    // Turns every "image" component into pixels before layout: the picture is what decides the
    // element height, and one that cannot be found must not cost us the rest of the lesson.
    const resolveAiImages = async (components, subject) => {
        const resolved = {};
        for (let index = 0; index < components.length; index++) {
            const component = components[index];
            if (component.type !== 'image') continue;

            try {
                // Reuse whatever the preview already fetched; the "simple" type skips the
                // preview entirely and resolves here for the first time.
                const entry =
                    aiResolvedImagesRef.current[index] && aiResolvedImagesRef.current[index].dataUrl
                        ? aiResolvedImagesRef.current[index]
                        : await resolveIllustration(component, { subject });
                if (!entry || !entry.dataUrl) continue;
                aiResolvedImagesRef.current[index] = entry;
                resolved[index] = entry;
            } catch (error) {
                console.error('Error resolving AI illustration:', error);
            }
        }
        return resolved;
    };

    const handleAddToBoard = async (response) => {
        if (!response || !response.components) return;

        // The subject the response was generated for, not whatever the dropdown shows now.
        const resolvedImages = await resolveAiImages(response.components, response.metadata?.subject);

        const currentColor = store.getState().lessonState?.color || '#000000';
        const SPACING = 15;
        const CANVAS_HEIGHT = 720;
        const COL_START_X = 20;
        let nextY = 20;
        let colX = COL_START_X;
        let colMaxWidth = 0;

        const elements = [];
        for (let index = 0; index < response.components.length; index++) {
            const component = response.components[index];
            try {
                let width = component.width || 300;
                let height = component.height || 50;

                // For text components, estimate height from content length, font size and width
                if (component.type === 'text') {
                    // fontSize is % of canvas height; smaller fonts = more chars per line
                    const fontPct = component.fontSize || 3.5;
                    const fontPx = Math.round(CANVAS_HEIGHT * fontPct / 100);
                    const charsPerLine = Math.max(1, Math.floor(width / (fontPx * 0.55)));
                    const lineCount = Math.max(1, Math.ceil(component.content.length / charsPerLine));
                    height = Math.max(height, lineCount * (fontPx + 4) + 10);
                }

                if (component.type === 'image') {
                    const resolved = resolvedImages[index];
                    // Generation failed: skip it, the rest of the lesson still lands.
                    if (!resolved) continue;
                    if (resolved.naturalWidth > 0 && resolved.naturalHeight > 0) {
                        height = Math.round(width * (resolved.naturalHeight / resolved.naturalWidth));
                    }
                }

                // Wrap to next column if this element would go below canvas
                if (nextY + height > CANVAS_HEIGHT && nextY > 20) {
                    colX = colX + colMaxWidth + SPACING;
                    nextY = 20;
                    colMaxWidth = 0;
                }

                const x = colX;
                const y = nextY;
                nextY = y + height + SPACING;
                colMaxWidth = Math.max(colMaxWidth, width);

                if (component.type === 'text') {
                    const opts = {};
                    if (component.fontSize != null) opts.fontSize = component.fontSize;
                    if (component.fontFamily) opts.fontFamily = component.fontFamily;
                    if (component.fontWeight) opts.fontWeight = component.fontWeight;
                    if (component.fontStyle) opts.fontStyle = component.fontStyle;
                    const el = lessonList.buildTextElement(
                        component.content,
                        x,
                        y,
                        width,
                        height,
                        1,
                        currentColor,
                        boardManager.email,
                        Object.keys(opts).length > 0 ? opts : undefined,
                    );
                    elements.push(el);
                } else if (component.type === 'latex') {
                    const el = lessonList.buildLatexElement(
                        component.content,
                        x,
                        y,
                        width,
                        height,
                        1,
                        currentColor,
                        boardManager.email,
                    );
                    addLatexToWindowCache(el.id, window.latexToImg(component.content));
                    elements.push(el);
                } else if (component.type === 'svg') {
                    const el = lessonList.buildEmbeddedSvgElement(
                        component.content,
                        x,
                        y,
                        width,
                        height,
                        1,
                        currentColor,
                        boardManager.email,
                    );
                    elements.push(el);
                } else if (component.type === 'image') {
                    // Uploads to the same sticky-image path as a pasted picture, so the
                    // signed URL re-hydrates when the board is reloaded.
                    const asset = await boardManager.uploadStickyImageAsset(resolvedImages[index].dataUrl);
                    if (asset) {
                        const el = lessonList.buildImageElement(
                            asset.id,
                            x,
                            y,
                            width,
                            height,
                            boardManager.email,
                            asset.url,
                        );
                        elements.push(el);
                    }
                }
            } catch (error) {
                console.error('Error building component:', error);
            }
        }

        if (elements.length > 0) {
            await boardManager.addBatchStickyElements(elements);
        }

        setAiSnackbar({
            open: true,
            message: props.intl.formatMessage({ id: 'ai.dialog.addedToBoard' }),
            severity: 'success',
        });
        handleCloseAiPopover();
    };

    if (!boardManager.isAdmin) {
        return (
            <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                <Hidden mdUp implementation='css'>
                    <Fab
                        size='small'
                        color={props.lessonState.raisedHand ? 'primary' : 'default'}
                        onClick={raiseHandClick}
                    >
                        <HandRaisedIcon />
                    </Fab>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <Button
                        variant='contained'
                        color={props.lessonState.raisedHand ? 'primary' : 'default'}
                        size='small'
                        startIcon={<HandRaisedIcon />}
                        onClick={raiseHandClick}
                    >
                        <FormattedMessage id='menu.raiseHand' />
                    </Button>
                </Hidden>
            </div>
        );
    }

    const resetToPen = () => {
        store.dispatch(changeTool(TOOL_PEN));
        if (window.switchToStickySelection) {
            window.switchToStickySelection(false);
        }
        if (window.toolSelectionCallback) {
            window.toolSelectionCallback(TOOL_PEN);
        }
    };

    const switchToSplitBoards = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.startIndividualBoards);
        boardManager.switchToNewMultiBoardForAdmin();
        resetToPen();
    };

    const resumeIndividualBoard = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.resumeIndividualBoards);
        boardManager.resumeMultiBoardForAdmin();
        resetToPen();
    };

    const switchToSingleBoard = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.stopIndividualBoards);
        boardManager.switchToSingleBoardForAdmin();
        resetToPen();
    };

    const pauseMultiBoard = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.stopIndividualBoards);
        boardManager.switchToSingleBoardForAdmin(true);
        resetToPen();
    };
    const displayShareDialog = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.share);
        store.dispatch(shareLesson({ id: window.location.pathname.split('/')[2] }));
    };
    const showJoinRequests = () => {
        window.logAppActivity(GA_EVENT_TREE.lessonPage.menuActions.requests);
        store.dispatch(userJoinRequest({ classId: window.location.pathname.split('/')[2], userId: null }));
    };

    let activeMemberCount = 0;
    if (props.lessonState && props.lessonState.members) {
        props.lessonState.members.forEach((member) => {
            if (member.online) {
                activeMemberCount++;
            }
        });
    }

    if (!boardManager.lessonState) return null;
    if (!props?.lessonState) return null;

    let multiBoardOption;
    if (props?.lessonState?.boardType === BOARD_TYPE.MULTI_BOARD) {
        multiBoardOption = (
            <>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.pauseBoard.tooltip' />}>
                        <IconButton size='small' color='primary' className='u-ml-half' onClick={pauseMultiBoard}>
                            <Pause />
                        </IconButton>
                    </VBToolTip>
                    <VBToolTip content={<FormattedMessage id='menu.stopBoard.tooltip' />}>
                        <IconButton size='small' color='primary' className='u-ml-half' onClick={switchToSingleBoard}>
                            <Stop />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.pauseBoard.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<Pause />}
                            onClick={pauseMultiBoard}
                            className='u-ml-half'
                        >
                            <FormattedMessage id='menu.pauseBoard' />
                        </Button>
                    </VBToolTip>
                    <VBToolTip content={<FormattedMessage id='menu.stopBoard.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<Stop />}
                            onClick={switchToSingleBoard}
                            className='u-ml-half'
                        >
                            <FormattedMessage id='menu.stopBoard' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </>
        );
    } else if (props?.lessonState?.boardType === BOARD_TYPE.SINGLE_BOARD && !boardManager.lessonState.pausedBoard) {
        multiBoardOption = (
            <>
                <Hidden mdUp implementation='css'>
                    <VBToolTip
                        content={
                            activeMemberCount === 0 ? (
                                <FormattedMessage id='menu.splitBoard.tooltip.disabled' />
                            ) : (
                                <FormattedMessage id='menu.splitBoard.tooltip' />
                            )
                        }
                    >
                        <IconButton
                            size='small'
                            color='primary'
                            className='u-ml-half'
                            onClick={switchToSplitBoards}
                            disabled={activeMemberCount === 0}
                        >
                            <MultiBoardIcon />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip
                        content={
                            activeMemberCount === 0 ? (
                                <FormattedMessage id='menu.splitBoard.tooltip.disabled' />
                            ) : (
                                <FormattedMessage id='menu.splitBoard.tooltip' />
                            )
                        }
                    >
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<MultiBoardIcon />}
                            onClick={switchToSplitBoards}
                            className='u-ml-half'
                            disabled={activeMemberCount === 0}
                        >
                            <FormattedMessage id='menu.splitBoard' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </>
        );
    } else {
        multiBoardOption = (
            <>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.resumeBoard' />}>
                        <IconButton
                            size='small'
                            color='primary'
                            className='u-ml-half'
                            onClick={resumeIndividualBoard}
                            disabled={activeMemberCount === 0}
                        >
                            <PlayArrow />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.resumeBoard.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<PlayArrow />}
                            onClick={resumeIndividualBoard}
                            className='u-ml-half'
                            disabled={activeMemberCount === 0}
                        >
                            <FormattedMessage id='menu.resumeBoard' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </>
        );
    }

    return (
        <>
            {props?.lessonState?.openJoinRequests?.length > 0 ? (
                <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                    <Badge badgeContent={props?.lessonState?.openJoinRequests?.length} color='secondary'>
                        <IconButton size='small' color='primary' onClick={showJoinRequests}>
                            <DraftsIcon />
                        </IconButton>
                    </Badge>
                </div>
            ) : null}

            <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.aiExercise.tooltip' />}>
                        <IconButton
                            size='small'
                            color='primary'
                            onClick={handleAiButtonClick}
                            disabled={aiLoading}
                        >
                            <AIAssistantIcon />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.aiExercise.tooltip' />}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<AIAssistantIcon />}
                            onClick={handleAiButtonClick}
                            disabled={aiLoading}
                        >
                            <FormattedMessage id='menu.aiExercise' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </div>

            <div style={{ padding: '0.1rem 0.5rem 0.1rem 0.5rem', borderRight: '1px solid rgba(0, 0, 0, 0.1' }}>
                <Hidden mdUp implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.shareLesson.tooltip' />}>
                        <IconButton size='small' color='primary' onClick={displayShareDialog}>
                            <PersonAddIcon />
                        </IconButton>
                    </VBToolTip>
                </Hidden>
                <Hidden only={['xs', 'sm']} implementation='css'>
                    <VBToolTip content={<FormattedMessage id='menu.shareLesson.tooltip' />}>
                        <Button
                            variant='contained'
                            color='primary'
                            size='small'
                            startIcon={<PersonAddIcon />}
                            onClick={displayShareDialog}
                        >
                            <FormattedMessage id='menu.shareLesson' />
                        </Button>
                    </VBToolTip>
                </Hidden>
            </div>
            {multiBoardOption}

            <Popover
                open={Boolean(aiAnchorEl)}
                anchorEl={aiAnchorEl}
                onClose={handleCloseAiPopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Paper style={{ width: '550px', maxWidth: '90vw' }}>
                    <Box p={2}>
                        <Box display='flex' justifyContent='space-between' alignItems='center' mb={1.5}>
                            <Box fontWeight={600} fontSize='1rem'>
                                <FormattedMessage id='menu.aiExercise' />
                            </Box>
                            <MuiIconButton size='small' onClick={handleCloseAiPopover}>
                                <CloseIcon fontSize='small' />
                            </MuiIconButton>
                        </Box>

                        <Box display='flex' mb={2} flexWrap='wrap' style={{ gap: '8px' }}>
                            <FormControl size='small'>
                                <Select
                                    value={aiLanguage}
                                    onChange={(e) => {
                                        const newLang = e.target.value;
                                        setAiLanguage(newLang);
                                        if (props.updateLanguage) {
                                            props.updateLanguage(newLang);
                                        }
                                    }}
                                    variant='outlined'
                                    className={aiSelectClasses.select}
                                    autoWidth
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                        getContentAnchorEl: null,
                                    }}
                                    renderValue={(value) => (
                                        <Box display='flex' alignItems='center' justifyContent='center'>
                                            {value === 'ro' && <RomaniaIcon />}
                                            {value === 'en' && <UKIcon />}
                                            {value === 'fr' && <FranceIcon />}
                                        </Box>
                                    )}
                                >
                                    <MenuItem value='ro'><RomaniaIcon /> Română</MenuItem>
                                    <MenuItem value='en'><UKIcon /> English</MenuItem>
                                    <MenuItem value='fr'><FranceIcon /> Français</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size='small'>
                                <Select
                                    value={aiSubject}
                                    onChange={(e) => setAiSubject(e.target.value)}
                                    variant='outlined'
                                    className={aiSelectClasses.select}
                                    autoWidth
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                        getContentAnchorEl: null,
                                    }}
                                    renderValue={(value) => (
                                        <Box style={{ fontSize: '0.75rem' }}>
                                            <FormattedMessage id={`ai.subject.${value}`} />
                                        </Box>
                                    )}
                                >
                                    <MenuItem value='math'><FormattedMessage id='ai.subject.math' /></MenuItem>
                                    <MenuItem value='chemistry'><FormattedMessage
                                        id='ai.subject.chemistry' /></MenuItem>
                                    <MenuItem value='physics'><FormattedMessage id='ai.subject.physics' /></MenuItem>
                                    <MenuItem value='geography'><FormattedMessage
                                        id='ai.subject.geography' /></MenuItem>
                                    <MenuItem value='history'><FormattedMessage id='ai.subject.history' /></MenuItem>
                                    <MenuItem value='language'><FormattedMessage id='ai.subject.language' /></MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size='small'>
                                <Select
                                    value={aiGradeLevel}
                                    onChange={(e) => setAiGradeLevel(e.target.value)}
                                    variant='outlined'
                                    className={aiSelectClasses.select}
                                    autoWidth
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                        getContentAnchorEl: null,
                                    }}
                                    renderValue={(value) => (
                                        <Box style={{ fontSize: '0.75rem' }}>
                                            <FormattedMessage id='ai.grade' values={{ grade: value }} />
                                        </Box>
                                    )}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(grade => (
                                        <MenuItem key={grade} value={String(grade)}>
                                            <FormattedMessage id='ai.grade' values={{ grade }} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size='small'>
                                <Select
                                    value={aiType}
                                    onChange={(e) => setAiType(e.target.value)}
                                    variant='outlined'
                                    className={aiSelectClasses.select}
                                    autoWidth
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                        getContentAnchorEl: null,
                                    }}
                                    renderValue={(value) => (
                                        <Box style={{ fontSize: '0.75rem' }}>
                                            <FormattedMessage id={`ai.type.${value}`} />
                                        </Box>
                                    )}
                                >
                                    <MenuItem value='simple'><FormattedMessage id='ai.type.simple.full' /></MenuItem>
                                    <MenuItem value='solution'><FormattedMessage
                                        id='ai.type.solution.full' /></MenuItem>
                                    <MenuItem value='concept'><FormattedMessage id='ai.type.concept.full' /></MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {!aiResponse && (
                            <Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant='outlined'
                                    placeholder={props.intl.formatMessage({ id: `ai.prompt.${aiType}` })}
                                    value={aiPrompt}
                                    onChange={(e) => setAiPrompt(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey && !aiLoading) {
                                            e.preventDefault();
                                            handleSendPrompt();
                                        }
                                    }}
                                    disabled={aiLoading}
                                />
                                <Box mt={2} display='flex' justifyContent='flex-end'>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        size='small'
                                        endIcon={aiLoading ? <CircularProgress size={16} color='inherit' /> :
                                            <SendIcon fontSize='small' />}
                                        onClick={handleSendPrompt}
                                        disabled={aiLoading || !aiPrompt.trim()}
                                        style={{ padding: '6px 16px' }}
                                    >
                                        <FormattedMessage id={aiLoading ? 'ai.button.generating' : 'ai.button.send'} />
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {aiResponse && (
                            <Box>
                                <AIResponseRenderer
                                    title={aiResponse.title}
                                    components={aiResponse.components}
                                    subject={aiResponse.metadata?.subject}
                                    resolvedImages={aiResolvedImagesRef.current}
                                    onImageResolved={handleImageResolved}
                                />
                                <Box mt={2} display='flex' justifyContent='flex-end'>
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        onClick={() => {
                                            setAiResponse(null);
                                            setAiPrompt('');
                                            aiResolvedImagesRef.current = {};
                                        }}
                                        style={{ padding: '6px 16px' }}
                                    >
                                        New Request
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Popover>

            <Dialog
                open={aiResponseDialogOpen}
                onClose={() => setAiResponseDialogOpen(false)}
                maxWidth='md'
                fullWidth
            >
                <DialogTitle>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Box fontWeight={600}>
                            {aiResponse?.title || 'AI Response'}
                        </Box>
                        <MuiIconButton size='small' onClick={() => setAiResponseDialogOpen(false)}>
                            <CloseIcon fontSize='small' />
                        </MuiIconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {aiResponse && (
                        <AIResponseRenderer
                            title={null}
                            components={aiResponse.components}
                            subject={aiResponse.metadata?.subject}
                            resolvedImages={aiResolvedImagesRef.current}
                            onImageResolved={handleImageResolved}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAiResponseDialogOpen(false)}
                        color='default'
                        size='small'
                    >
                        <FormattedMessage id='ai.dialog.close' />
                    </Button>
                    <Button
                        onClick={async () => {
                            await handleAddToBoard(aiResponse);
                            setAiResponseDialogOpen(false);
                        }}
                        color='primary'
                        variant='contained'
                        size='small'
                    >
                        <FormattedMessage id='ai.dialog.addToBoard' />
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={aiSnackbar.open}
                autoHideDuration={10000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MuiAlert onClose={handleCloseSnackbar} severity={aiSnackbar.severity} elevation={6} variant='filled'>
                    {aiSnackbar.message}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

const mapStateToProps = (state) => {
    const { drawing, lessonState, profile, mainAppState } = state;
    return { drawing, lessonState, profile, subscriptions: mainAppState.subscriptions };
};

export default injectIntl(connect(mapStateToProps)(ActionButtons));
